import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Marker, Circle } from "react-leaflet";
import LeafletMap, { liveDotIcon, Recenter } from "../components/LeafletMap";
import { Car, Clock, AlertTriangle } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function fmtCountdown(s) {
  if (s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}
function fmtIST(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
}

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [secs, setSecs] = useState(0);
  const tickRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/share/${token}`);
      setData(data);
      setSecs(data.seconds_remaining);
      setErr("");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) setErr("expired");
      else setErr("notfound");
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  if (err === "expired" || err === "notfound") {
    return (
      <div data-testid="share-error" className="min-h-[100dvh] flex flex-col items-center justify-center p-8 text-center bg-white">
        <AlertTriangle size={40} className="text-alert mb-3" />
        <h1 className="text-2xl font-black text-slate-900">
          {err === "expired" ? "This share link has expired" : "Link not found"}
        </h1>
        <p className="mt-2 text-slate-500 text-sm">Ask the car owner to create a new one.</p>
        <div className="mt-6 text-xs text-slate-400">Powered by CarTrack</div>
      </div>
    );
  }

  if (!data)
    return (
      <div data-testid="share-loading" className="min-h-[100dvh] flex items-center justify-center text-slate-400">
        Loading live location…
      </div>
    );

  const loc = data.last_location;
  const center = loc ? [loc.latitude, loc.longitude] : [28.6139, 77.209];
  const speed = Math.round(loc?.speed || 0);
  const moving = speed >= 5;

  return (
    <div data-testid="share-view" className="relative h-[100vh] h-[100dvh] w-full overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-[450] px-4 pt-4">
        <div className="glass-card rounded-2xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <Car size={18} color="white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Live car location</div>
              <div className="font-extrabold text-slate-900 truncate" data-testid="share-car-name">
                {data.car_name}
              </div>
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-primary-light text-primary"
            data-testid="share-countdown"
          >
            <Clock size={14} /> {fmtCountdown(secs)}
          </div>
        </div>
      </div>

      <div className="absolute inset-0">
        <LeafletMap center={center} zoom={14} testid="share-map">
          {loc && (
            <>
              <Marker position={[loc.latitude, loc.longitude]} icon={liveDotIcon} />
              <Recenter lat={loc.latitude} lon={loc.longitude} zoom={14} />
            </>
          )}
        </LeafletMap>
      </div>

      <div className="absolute left-3 right-3 bottom-4 z-[450]">
        <div className="glass-card rounded-2xl p-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Speed" value={`${speed}`} suffix="km/h" />
            <Stat label="Status" value={moving ? "Moving" : "Parked"} color={moving ? "text-moving" : "text-parked"} />
            <Stat label="Updated" value={fmtIST(loc?.timestamp)} />
          </div>
          <div className="mt-2 text-center text-[11px] text-slate-400">
            Powered by CarTrack — link auto-expires
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, suffix, color = "text-slate-900" }) {
  return (
    <div>
      <div className={`font-black text-lg ${color} leading-tight`}>
        {value}
        {suffix && <span className="text-[11px] font-bold ml-0.5 text-slate-500">{suffix}</span>}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
